/**
 * Shared utilities for mark handling across the application
 * Provides consistent mark colors, types, and icon generation
 */

/**
 * Get mark color based on label/kind
 * SS, SP, FS, FP marks are orange, all others are white
 */
export const getMarkColor = (label) => {
    if (!label) return '#ffffff'; // Default white for unknown marks
    const l = label.toLowerCase();

    // Start/Finish marks are orange
    if (l.includes('ss') || l.includes('sp') || l.includes('fs') || l.includes('fp') || l.includes('c') || l.includes('s')) {
        return '#ff9800'; // Orange
    }

    // All other marks are yellow
    return '#ffff00';
};

/**
 * Get mark type based on label/kind
 */
export const getMarkType = (label) => {
    if (!label) return 'mark';
    const l = label.toLowerCase();

    if (l.includes('ss') || l.includes('sp')) return 'start';
    if (l.includes('fs') || l.includes('fp')) return 'finish';
    if (l.includes('1') || l.includes('w')) return 'windward';
    if (l.includes('4') || l.includes('l')) return 'leeward';
    if (l.includes('2') || l.includes('3')) return 'reach';

    return 'mark';
};

/**
 * Generate Google Maps marker icon for course marks
 * Implements the visual design:
 * - Ideal marks: Grey fill with colored border
 * - Actual marks: Colored fill with grey border
 * - Selected marks: Yellow border
 */
export const getMarkIcon = (mark, isSelected = false, isActualPosition = false) => {
    const baseSize = 12;
    const size = isSelected ? baseSize + 4 : baseSize;
    const markColor = getMarkColor(mark.label);

    let fillColor, fillOpacity, strokeColor, strokeWeight;

    if (isActualPosition) {
        // ACTUAL MARKS: Colored fill with grey border
        fillColor = markColor;
        fillOpacity = 0.9;
        strokeColor = '#757575'; // Grey border
        strokeWeight = 3;
    } else {
        // IDEAL MARKS: Grey fill with colored border
        fillColor = '#bdbdbd'; // Light grey fill
        fillOpacity = 0.8;
        strokeColor = markColor; // Colored border (orange for start/finish, white for others)
        strokeWeight = 3;       
    }

    // Selected marks get red border regardless of type
    if (isSelected) {
        strokeColor = '#ff0000'; // Red for selected
        strokeWeight = 4;
    }

    return {
        path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
        fillColor: fillColor,
        fillOpacity: fillOpacity,
        scale: size,
        strokeColor: strokeColor,
        strokeWeight: strokeWeight,
    };
};

/**
 * Legacy getMarkColor function for backward compatibility
 * Used in courseCalculator.js and other files that use the old color scheme
 */
export const getLegacyMarkColor = (label) => {
    if (!label) return 'purple';
    const l = label.toLowerCase();
    
    if (l.includes('ss') || l.includes('sp')) return 'orange';
    if (l.includes('w') || l === '1') return 'blue';
    if (l.includes('l') || l.includes('4')) return 'green';
    if (l.includes('f')) return 'red';
    
    return 'purple';
};

/**
 * Legacy getMarkType function for backward compatibility
 * Used in courseCalculator.js and other files
 */
export const getLegacyMarkType = (label) => {
    if (!label) return 'mark';
    const l = label.toLowerCase();
    
    if (l.includes('ss') || l.includes('sp')) return 'start';
    if (l.includes('w') || l === '1') return 'windward';
    if (l.includes('l') || l.includes('4')) return 'leeward';
    if (l.includes('f')) return 'finish';
    
    return 'mark';
};